import { Link } from "@tanstack/react-router";
import { Badge, Skeleton } from "@/components/kit";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import type {
  MarketingRoiResultDTO,
  MarketingRoiRowDTO,
} from "../../domain/types";
import { ReportMobileCard, ReportStat } from "./ReportProductLink";
import { ReportTermHint } from "./ReportTermHint";

type MarketingRoiTableProps = {
  result?: MarketingRoiResultDTO;
  isLoading?: boolean;
};

function displayValue(value: string | null | undefined, fallback: string) {
  return value?.trim() ? value : fallback;
}

function roasBadgeVariant(
  roas: string | null,
): "destructive" | "default" | "secondary" {
  if (!roas) return "secondary";
  const value = Number(roas);
  if (value < 1) return "destructive";
  if (value >= 2) return "default";
  return "secondary";
}

function RoasBadge({ roas }: { roas: string | null }) {
  if (!roas) {
    return (
      <Badge variant="secondary" className="rounded-full">
        —
      </Badge>
    );
  }

  return (
    <Badge
      variant={roasBadgeVariant(roas)}
      className="rounded-full tabular-nums"
    >
      {roas}x
    </Badge>
  );
}

function formatMetric(value: string | null) {
  if (value == null) return "—";
  return formatLak(value);
}

function attributionLabel(level: string) {
  switch (level) {
    case "product":
      return "ສິນຄ້າ";
    case "category":
      return "ຫມວດ";
    default:
      return level;
  }
}

function rowKey(row: MarketingRoiRowDTO) {
  return [
    row.platform,
    row.campaignName,
    row.mappedProductId,
    row.mappedCategoryId,
  ].join(":");
}

function MarketingRoiAttribution({ row }: { row: MarketingRoiRowDTO }) {
  const unmapped = row.attributionLevel === "none";

  return (
    <div className="flex flex-col gap-1">
      <span>{displayValue(row.mappedProductName, "ບໍ່ຜູກສິນຄ້າ")}</span>
      <span className="text-muted-foreground text-xs">
        {displayValue(row.mappedCategoryName, "ບໍ່ຜູກຫມວດ")}
      </span>
      {unmapped ? (
        <Link
          to="/app/expenses"
          className="text-secondary text-xs underline-offset-2 hover:underline"
        >
          ໃສ່ mappedProductId
        </Link>
      ) : (
        <span className="text-muted-foreground text-xs">
          {attributionLabel(row.attributionLevel)}
        </span>
      )}
    </div>
  );
}

function MarketingRoiMobileCard({ row }: { row: MarketingRoiRowDTO }) {
  return (
    <ReportMobileCard>
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">
          {displayValue(row.campaignName, "ບໍ່ມີ campaign")}
        </span>
        <span className="text-muted-foreground text-xs">
          {displayValue(row.platform, "ບໍ່ມີແພລດຟອມ")}
        </span>
      </div>
      <div className="mt-2 text-sm">
        <MarketingRoiAttribution row={row} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat
          label="ຄ່າແອດ"
          value={formatLak(row.adSpend)}
          valueClassName="font-display text-secondary text-lg"
        />
        <ReportStat
          label={
            <ReportTermHint
              label="ROAS"
              hint="Return on Ad Spend = ລາຍຮັບ ÷ ຄ່າແອດ"
            />
          }
          value={<RoasBadge roas={row.roas} />}
        />
        <ReportStat label="ລາຍຮັບ" value={formatMetric(row.attributedRevenue)} />
        <ReportStat label="ກຳໄລຂັ້ນຕົ້ນ" value={formatMetric(row.grossProfit)} />
        <ReportStat
          label="ກຳໄລຫຼັງແອດ"
          value={formatMetric(row.profitAfterAd)}
          className="col-span-2"
        />
        <ReportStat
          label="ຊ່ວງເວລາ"
          value={`${formatDateLocal(row.firstSpentAt)} - ${formatDateLocal(row.lastSpentAt)}`}
          valueClassName="text-muted-foreground text-xs font-normal"
          className="col-span-2"
        />
      </dl>
    </ReportMobileCard>
  );
}

function MarketingRoiDesktopRow({ row }: { row: MarketingRoiRowDTO }) {
  return (
    <tr className="border-secondary/10 border-t">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="font-medium">
            {displayValue(row.campaignName, "ບໍ່ມີ campaign")}
          </span>
          <span className="text-muted-foreground text-xs">
            {displayValue(row.platform, "ບໍ່ມີແພລດຟອມ")}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <MarketingRoiAttribution row={row} />
      </td>
      <td className="px-4 py-3 text-right font-display text-secondary text-xl tabular-nums">
        {formatLak(row.adSpend)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {formatMetric(row.attributedRevenue)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {formatMetric(row.grossProfit)}
      </td>
      <td className="px-4 py-3 text-right">
        <RoasBadge roas={row.roas} />
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {formatMetric(row.profitAfterAd)}
      </td>
      <td className="px-4 py-3 text-muted-foreground text-xs">
        {formatDateLocal(row.firstSpentAt)} - {formatDateLocal(row.lastSpentAt)}
      </td>
    </tr>
  );
}

export function MarketingRoiTable({
  result,
  isLoading,
}: MarketingRoiTableProps) {
  const rows = result?.data ?? [];

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }

  return (
    <section className="brand-card overflow-hidden">
      <div className="flex flex-col gap-1 p-4 sm:p-5">
        <h2 className="font-display text-lg">ROI ການຕລາດ</h2>
        <p className="text-muted-foreground text-sm">
          ROAS ແລະ ກຳໄລຫຼັງຄ່າແອດ — ຜູກຕາມສິນຄ້າ/ຫມວດທີ່ map ໃນ expenses
        </p>
        {result?.meta && result.meta.unmappedCount > 0 ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {result.meta.unmappedCount} campaign ຍັງບໍ່ map (
              {formatLak(result.meta.unmappedSpend)})
            </span>
            <Link
              to="/app/expenses"
              className="text-secondary underline-offset-2 hover:underline"
            >
              ໄປໃສ່ mapping →
            </Link>
          </div>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="border-secondary/15 border-t p-8 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນ campaign ໃນຊ່ວງນີ້
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 border-secondary/15 border-t p-4 md:hidden">
            {rows.map((row) => (
              <MarketingRoiMobileCard key={rowKey(row)} row={row} />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">Campaign</th>
                  <th className="px-4 py-3 font-medium">ການຜູກ</th>
                  <th className="px-4 py-3 text-right font-medium">ຄ່າແອດ</th>
                  <th className="px-4 py-3 text-right font-medium">ລາຍຮັບ</th>
                  <th className="px-4 py-3 text-right font-medium">ກຳໄລຂັ້ນຕົ້ນ</th>
                  <th className="px-4 py-3 text-right font-medium">
                    <ReportTermHint
                      label="ROAS"
                      hint="Return on Ad Spend = ລາຍຮັບ ÷ ຄ່າແອດ"
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    ກຳໄລຫຼັງແອດ
                  </th>
                  <th className="px-4 py-3 font-medium">ຊ່ວງເວລາ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <MarketingRoiDesktopRow key={rowKey(row)} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
